/**
 * Generate the studio's theme stylesheet from the variable registry.
 *
 * Emits a dark base + a light override block, keyed off `<html>`'s
 * `data-ows-theme`:
 *
 *   :root, :root[data-ows-theme="dark"]   { …dark base… }
 *   :root[data-ows-theme="light"]         { …light values… }
 *
 * The app has ONE brand colour (orange #ff791b), so there are no accent variants.
 * Every consuming token carries the literal as a `var(name, fallback)`, so a
 * surface rendered before this sheet mounts still shows the dark value — this
 * sheet only adds the switchable light layer.
 */

import { getRegistry } from './themeRegistry';
import { lightValue } from './themePalette';

// `import './tokens'` (its side effect: every themed()/themedValue() call) must
// have run so the registry is populated before this builds. tokens.ts is imported
// across the app long before the theme stylesheet renders, but import it here too
// so the builder is self-sufficient and order-independent.
import './tokens';
// The customizer wizard keeps its palette in a sibling token file. It's a lazy
// route, so its themed() calls would not have run by the time this builds at boot;
// import it for its side effect so its `--ows-cust-*` vars register too.
import './customizer-tokens';
// FTUX - Getting Started (src/nova/features/ftux) is a lazy chunk too: its --ows-ftux-* vars register here.
import './ftux-tokens';
// The configurator-v3 wizard (the immersive "build your casino" flow) is the same
// case: its base palette lives here as a sibling token file, and its per-step token
// files are barrelled for registration. Both are side-effect-imported so every
// `--ows-cfg-*` var registers before this builds.
import './configurator-tokens';
import '@/nova/pages/configurator-v3/registerThemeTokens';
// The Mexican-market forks reuse v3's groups verbatim (already registered above, and
// re-registration is a no-op), so only the group they ADD needs pulling in: the SPEI checkout
// surface. Importing the single token file rather than a whole fork barrel keeps the boot bundle
// from re-evaluating eight identical groups.
import '@/nova/pages/configurator-v3-mx/steps/finish/spei.tokens';

const block = (selector: string, decls: string[]): string =>
  decls.length === 0 ? '' : `${selector}{${decls.join('')}}`;

const decl = (name: string, value: string): string => `${name}:${value};`;

/** Build the full stylesheet text. Pure (no DOM); cached by the caller. */
export const buildThemeCss = (): string => {
  const entries = getRegistry();
  return [
    block(':root,:root[data-ows-theme="dark"]', entries.map((e) => decl(e.name, e.dark))),
    block(':root[data-ows-theme="light"]', entries.map((e) => decl(e.name, lightValue(e.name, e.dark)))),
  ]
    .filter(Boolean)
    .join('\n');
};
